// @ts-nocheck
import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Divider from '@mui/material/Divider';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Typography from '@mui/material/Typography';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import PeopleIcon from '@mui/icons-material/People';
import ShareIcon from '@mui/icons-material/Share';
import EventIcon from '@mui/icons-material/Event';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import BusinessIcon from '@mui/icons-material/Business';
import TipsAndUpdatesIcon from '@mui/icons-material/TipsAndUpdates';
import LogoutIcon from '@mui/icons-material/Logout';

import { Link as RouterLink, useLocation, useNavigate, useParams } from 'react-router-dom';
import useAuthentication from 'utils/useAuthentication';
import paths from 'paths';
import { resetAccessOrders } from 'store/slices/orders';

const Navigation = ({ sidebarWidth, mobileOpen, onMobileClose }) => {
    const { logout } = useAuthentication();
    const location = useLocation();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { organisationId } = useParams();
    const user = useSelector(state => state.auth.user);
    const organisations = user?.organisations || [];
    const organisation = organisations.find(org => org.organisationId === organisationId);

    const handleOrgChange = (event) => {
        const newOrgId = event.target.value;
        dispatch(resetAccessOrders());
        navigate(paths.penlyAccess.getPath(newOrgId));
    };

    const accessBase = organisationId ? `/${organisationId}/access` : '';

    const navContent = (
        <>
            { /* Logo */ }
            <Box sx={{ height: 60, display: 'flex', alignItems: 'center', px: 2 }}>
                <RouterLink to={ organisationId ? paths.penlyAccess.getPath(organisationId) : '/' }>
                    <img style={{ height: 28 }} src="/TealBusinessLogo.svg" alt="Teal Business" />
                </RouterLink>
            </Box>

            <Divider />

            { /* Organisation dropdown */ }
            { organisations.length > 0 && (
                <Box sx={{ px: 2, py: 1.5 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                        Organisation
                    </Typography>
                    { organisations.length === 1 ? (
                        <Typography variant="body2">{ organisations[0]?.name }</Typography>
                    ) : (
                        <Select
                            fullWidth
                            size="small"
                            value={ organisationId || '' }
                            onChange={ handleOrgChange }
                            displayEmpty
                        >
                            { organisations.map(org => (
                                <MenuItem key={ org.organisationId } value={ org.organisationId }>
                                    { org.name }
                                </MenuItem>
                            )) }
                        </Select>
                    ) }
                </Box>
            ) }

            <Divider />

            { /* Main nav */ }
            { organisationId && organisation?.ready && (
                <List sx={{ flex: 1 }}>
                    <ListItemButton component={ RouterLink } to={ `${accessBase}` } selected={ location.pathname === accessBase || location.pathname === `${accessBase}/` } onClick={ onMobileClose }>
                        <ListItemIcon><ShoppingCartIcon /></ListItemIcon>
                        <ListItemText primary="Ordrer" />
                    </ListItemButton>
                    <ListItemButton component={ RouterLink } to={ `${accessBase}/brugere` } selected={ location.pathname.startsWith(`${accessBase}/brugere`) || location.pathname.startsWith(`${accessBase}/users`) } onClick={ onMobileClose }>
                        <ListItemIcon><PeopleIcon /></ListItemIcon>
                        <ListItemText primary="Brugere" />
                    </ListItemButton>
                    <ListItemButton component={ RouterLink } to={ `${accessBase}/delinger` } selected={ location.pathname.startsWith(`${accessBase}/delinger`) || location.pathname.startsWith(`${accessBase}/sharings`) } onClick={ onMobileClose }>
                        <ListItemIcon><ShareIcon /></ListItemIcon>
                        <ListItemText primary="Delinger" />
                    </ListItemButton>
                    <ListItemButton component={ RouterLink } to={ `${accessBase}/moeder` } selected={ location.pathname.startsWith(`${accessBase}/moeder`) } onClick={ onMobileClose }>
                        <ListItemIcon><EventIcon /></ListItemIcon>
                        <ListItemText primary="Møder" />
                    </ListItemButton>
                </List>
            ) }

            { /* Bottom section */ }
            <Box sx={{ mt: 'auto' }}>
                <Divider />
                <List dense>
                    <ListItemButton component={ RouterLink } to={ paths.account.getPath(organisationId) } onClick={ onMobileClose }>
                        <ListItemIcon><AccountCircleIcon /></ListItemIcon>
                        <ListItemText primary="Min konto" />
                    </ListItemButton>
                    { organisationId && (
                        <ListItemButton component={ RouterLink } to={ paths.organisation.getPath(organisationId) } onClick={ onMobileClose }>
                            <ListItemIcon><BusinessIcon /></ListItemIcon>
                            <ListItemText primary="Organisation" />
                        </ListItemButton>
                    ) }
                    { organisationId && (
                        <ListItemButton
                            component={ RouterLink }
                            to={ paths.newFeatures.getPath(organisationId) }
                            selected={ location.pathname.startsWith(paths.newFeatures.getPath(organisationId)) }
                            onClick={ onMobileClose }
                        >
                            <ListItemIcon><TipsAndUpdatesIcon /></ListItemIcon>
                            <ListItemText primary="Opdateringer" />
                        </ListItemButton>
                    ) }
                    <ListItemButton onClick={ logout }>
                        <ListItemIcon><LogoutIcon /></ListItemIcon>
                        <ListItemText primary="Log ud" />
                    </ListItemButton>
                </List>
            </Box>
        </>
    );

    return (
        <>
            <Drawer
                variant="temporary"
                open={ !!mobileOpen }
                onClose={ onMobileClose }
                ModalProps={{ keepMounted: true }}
                sx={{
                    display: { xs: 'block', md: 'none' },
                    '& .MuiDrawer-paper': {
                        width: sidebarWidth,
                        boxSizing: 'border-box',
                        display: 'flex',
                        flexDirection: 'column',
                        backgroundColor: '#ffffff',
                    },
                }}
            >
                { navContent }
            </Drawer>

            <Drawer
                variant="permanent"
                sx={{
                    width: sidebarWidth,
                    flexShrink: 0,
                    display: { xs: 'none', md: 'block' },
                    displayPrint: 'none',
                    '& .MuiDrawer-paper': {
                        width: sidebarWidth,
                        boxSizing: 'border-box',
                        display: 'flex',
                        flexDirection: 'column',
                        backgroundColor: '#ffffff',
                        borderRight: '1px solid',
                        borderColor: 'divider',
                    },
                }}
            >
                { navContent }
            </Drawer>
        </>
    );
};


export default Navigation;
